import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import SubjectIcon from "@mui/icons-material/Subject";
import MessageIcon from "@mui/icons-material/Message";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { AdminLayout } from "../../components/admin/AdminLayout";
import {
  useContactSubmissions,
  useMarkSubmissionViewed,
  useMarkSubmissionUnviewed,
} from "../../hooks/useContactSubmissions";
import type { ContactSubmissionResponse } from "../../api/generated/types.gen";

const getStatusColor = (status: string) => {
  switch (status) {
    case "new":
      return "primary";
    case "read":
      return "info";
    case "responded":
      return "success";
    case "archived":
      return "default";
    default:
      return "default";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const SubmissionCard: React.FC<{
  submission: ContactSubmissionResponse;
  onMarkViewed: (id: string) => void;
  onMarkUnviewed: (id: string) => void;
  isMarkingViewed: boolean;
  isMarkingUnviewed: boolean;
}> = ({ submission, onMarkViewed, onMarkUnviewed, isMarkingViewed, isMarkingUnviewed }) => {
  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        transition: "box-shadow 0.2s ease",
        "&:hover": {
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header with name, email, and status */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
              <PersonIcon sx={{ fontSize: "1.2rem", color: "text.secondary" }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {submission.name}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EmailIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {submission.email}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {!submission.viewed && (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  color: "success.main",
                  textTransform: "capitalize",
                }}
              >
                New
              </Typography>
            )}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: `${getStatusColor(submission.status)}.main`,
                textTransform: "capitalize",
              }}
            >
              {submission.status}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Contact details */}
        <Box sx={{ mb: 2 }}>
          {submission.phone && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 1,
              }}
            >
              <PhoneIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {submission.phone}
              </Typography>
            </Box>
          )}
          {submission.subject && (
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                mb: 1,
              }}
            >
              <SubjectIcon sx={{ fontSize: "1rem", color: "text.secondary", mt: 0.5 }} />
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                {submission.subject}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Message */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            mb: 2,
          }}
        >
          <MessageIcon
            sx={{
              fontSize: "1rem",
              color: "text.secondary",
              mt: 0.5,
              flexShrink: 0,
            }}
          />
          <Typography
            variant="body2"
            color="text.primary"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {submission.message}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Timestamp and Actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
            <Typography variant="caption" color="text.secondary">
              {formatDate(submission.created_at)}
            </Typography>
          </Box>
          {!submission.viewed && (
            <Tooltip title="Mark as viewed">
              <IconButton
                size="small"
                onClick={() => onMarkViewed(submission.id)}
                disabled={isMarkingViewed}
                sx={{
                  color: "success.main",
                  "&:hover": {
                    backgroundColor: "success.light",
                    color: "success.dark",
                  },
                }}
              >
                <VisibilityOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {submission.viewed && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <VisibilityIcon sx={{ fontSize: "1rem", color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Viewed
                </Typography>
              </Box>
              <Tooltip title="Mark as unviewed">
                <IconButton
                  size="small"
                  onClick={() => onMarkUnviewed(submission.id)}
                  disabled={isMarkingUnviewed}
                  sx={{
                    color: "warning.main",
                    "&:hover": {
                      backgroundColor: "warning.light",
                      color: "warning.dark",
                    },
                  }}
                >
                  <VisibilityOffIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export const ContactSubmissionsPage: React.FC = () => {
  const { data, isLoading, error } = useContactSubmissions();
  const markViewedMutation = useMarkSubmissionViewed();
  const markUnviewedMutation = useMarkSubmissionUnviewed();

  const handleMarkViewed = (submissionId: string) => {
    markViewedMutation.mutate(submissionId);
  };

  const handleMarkUnviewed = (submissionId: string) => {
    markUnviewedMutation.mutate(submissionId);
  };

  return (
    <AdminLayout>
      <Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 600,
            mb: 1,
          }}
        >
          Contact Submissions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
            mb: 4,
          }}
        >
          View and manage contact form submissions
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" color="error">
              Failed to load contact submissions. Please try again.
            </Typography>
          </Box>
        ) : !data || data.submissions.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <EmailIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
            <Typography variant="body1">No contact submissions yet</Typography>
          </Box>
        ) : (
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3 }}
            >
              {data.submissions.length} submission{data.submissions.length !== 1 ? "s" : ""}
            </Typography>
            {data.submissions.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                onMarkViewed={handleMarkViewed}
                onMarkUnviewed={handleMarkUnviewed}
                isMarkingViewed={markViewedMutation.isPending}
                isMarkingUnviewed={markUnviewedMutation.isPending}
              />
            ))}
          </Box>
        )}
      </Box>
    </AdminLayout>
  );
};
